import React, { useEffect, useMemo } from 'react';

import { defineMessages, useIntl } from 'react-intl-next';

import { useAISmartButtonActions } from '@atlassian/ai-smart-button-api';
import { type ConversationAction, usePromptCards } from '@atlassian/rovo-conversation-actions';

import { CommentCountsProvider } from '@confluence/comment-context/entry-points/CommentCountsContext.ts';
import { useViewPageRovoActions } from '@confluence/page-rovo-config-common/entry-points/useViewPageRovoActions.ts';
import {
	type RovoConfiguration,
	type RovoConfigurationProviderProps,
	type PageRovoConfigProviderProps,
	isPageRovoConfigProviderProps,
} from '@confluence/rovo-config-provider-common/entry-points/rovoConfigTypes.ts';
import { useRovoConfigProviderProps } from '@confluence/rovo-config-provider-common/entry-points/useRovoConfigProviderProps.ts';

import { useLiveDocNudgeRecommendationFilter } from './hooks/useLiveDocNudgeRecommendationFilter.ts';

import {
	useEditorRovoButtonActionsActions,
	useEditorRovoButtonActionsContext,
} from './store/EditorRovoButtonActionsStore.tsx';

import { getEmptyPageCreationActions } from './utils/getEmptyPageCreationActions.tsx';

import emptyPageGreetingIcon from './assets/empty-page-greeting-icon.svg';

const messages = defineMessages({
	emptyPageGreetingTitle: {
		id: 'edit-page-rovo-config-provider.empty-page-greeting-title',
		defaultMessage: 'Start your doc with a prompt',
		description: 'Greeting title shown in the Rovo empty state when the editor page is empty',
	},
});

const EditPageConfigProviderInner = ({ setConfiguration }: RovoConfigurationProviderProps) => {
	const viewPageRovoActions = useViewPageRovoActions();
	const editorActions = useEditorRovoButtonActionsActions();
	const editorActionsContext = useEditorRovoButtonActionsContext();
	const rovoConfigProviderProps = useRovoConfigProviderProps();
	const isEditorEmpty =
		isPageRovoConfigProviderProps(rovoConfigProviderProps) && rovoConfigProviderProps.isEditorEmpty;
	const { formatMessage } = useIntl();

	const { promptCards } = usePromptCards();

	const isEmptyPageWithPromptCards = isEditorEmpty && Array.isArray(promptCards);

	const pageProps = isPageRovoConfigProviderProps(rovoConfigProviderProps)
		? rovoConfigProviderProps
		: null;

	const contentType = pageProps?.contentType ?? '';
	const contentId = pageProps?.contentId ?? '';

	const enableLiveDocNudges = useLiveDocNudgeRecommendationFilter(contentType, contentId);
	const { setNudges } = useAISmartButtonActions();

	const config = useMemo<RovoConfiguration>(() => {
		return {
			getActions: async () => {
				if (isEmptyPageWithPromptCards) {
					return Promise.resolve(getEmptyPageCreationActions(promptCards));
				}

				if (editorActionsContext?.reference) {
					return Promise.resolve(editorActions);
				}

				const viewPageActions = viewPageRovoActions.map(
					(action: { action: ConversationAction }) => action.action,
				);

				return Promise.resolve([...editorActions, ...viewPageActions]);
			},
			customGreeting: isEmptyPageWithPromptCards
				? {
						iconSrc: emptyPageGreetingIcon,
						title: formatMessage(messages.emptyPageGreetingTitle),
					}
				: undefined,
			shouldShowGreeting: isEmptyPageWithPromptCards ? true : undefined,
			prioritizeCustomActionResults: isEmptyPageWithPromptCards ? true : undefined,
		};
	}, [
		editorActions,
		editorActionsContext,
		viewPageRovoActions,
		isEmptyPageWithPromptCards,
		promptCards,
		formatMessage,
	]);

	useEffect(() => {
		setConfiguration(config);

		if (!enableLiveDocNudges) {
			setNudges([]);
			return;
		}

		const nudges = [
			viewPageRovoActions.find((a: { actionId: string }) => a.actionId === 'audio-briefing')
				?.action,
		].filter(Boolean) as ConversationAction[];

		if (nudges.length > 0) {
			setNudges(nudges);
		}
	}, [
		config,
		setConfiguration,
		enableLiveDocNudges,
		viewPageRovoActions,
		setNudges,
		contentType,
		contentId,
	]);

	return null;
};

export const EditPageRovoConfigProvider = (props: RovoConfigurationProviderProps) => {
	const { contentId } = useRovoConfigProviderProps() as PageRovoConfigProviderProps;
	return (
		<CommentCountsProvider contentId={contentId}>
			<EditPageConfigProviderInner {...props} />
		</CommentCountsProvider>
	);
};
